import { useState } from 'react';
import Modal from '../Modal';
import Toggle from '../Toggle';
import Slider from '../Slider';
import Tabs from '@/components/ui/Tabs/Tabs';
import useConfigStore, { CONFIG_TABS } from '@/store/useConfigStore';
import useMetadataStore from '@/store/useMetadataStore';
import useTranslationStore, { SUPPORTED_LOCALES } from '@/store/useTranslationStore';
import { Asset } from '@rts';
import { Volume2, Eye, Gamepad2, Palette, Loader2, Check, Layers } from 'lucide-react';
import { useT } from '@/i18n';
import ThemeStackEditor from './ThemeStackEditor';
import './ConfigModal.css';

// Language options for the selector
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English', flag: '🇺🇸' },
  { value: 'es', label: 'Español', flag: '🇪🇸' },
  { value: 'pt', label: 'Português', flag: '🇧🇷' },
];

// Tab icons mapping
const TAB_ICONS = {
  audio: Volume2,
  display: Eye,
  gameplay: Gamepad2,
  theme: Palette,
};

// Fallback themes if metadata doesn't have themes yet
// Primary source: useMetadataStore.getThemes() from backend
const FALLBACK_THEMES = [
  {
    slug: 'base',
    nameKey: 'config.theme_classic',
    descKey: 'config.theme_classic_desc',
    preview: { primary: '#d4a94f', secondary: '#4dd9d9', bg: '#1a1a2e' },
  },
  {
    slug: 'dark',
    nameKey: 'config.theme_dark',
    descKey: 'config.theme_dark_desc',
    preview: { primary: '#3b82f6', secondary: '#f472b6', bg: '#0f172a' },
  },
  {
    slug: 'xhelos',
    nameKey: 'config.theme_xhelos',
    descKey: 'config.theme_xhelos_desc',
    preview: { primary: '#00ff9d', secondary: '#64FFDA', bg: '#0A192F' },
  },
  {
    slug: 'neon',
    nameKey: 'config.theme_neon',
    descKey: 'config.theme_neon_desc',
    preview: { primary: '#e930ff', secondary: '#00f5ff', bg: '#08080f' },
  },
  {
    slug: 'blood',
    nameKey: 'config.theme_blood',
    descKey: 'config.theme_blood_desc',
    preview: { primary: '#dc143c', secondary: '#ff6b6b', bg: '#1a0a0a' },
  },
];

export default function ConfigModal({ onClose, isSaving = false }) {
  const t = useT();
  const isOpen = useConfigStore((state) => state.isOpen);
  const activeTab = useConfigStore((state) => state.activeTab);
  const settings = useConfigStore((state) => state.settings);
  const closeModal = useConfigStore((state) => state.closeModal);
  const setTab = useConfigStore((state) => state.setTab);
  const updateSetting = useConfigStore((state) => state.updateSetting);
  const resetToDefaults = useConfigStore((state) => state.resetToDefaults);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      closeModal();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('config.title')}
      className="config-modal"
    >
      <Tabs
        variant="config"
        activeTab={activeTab}
        onTabChange={setTab}
        tabs={[
          {
            id: 'audio',
            label: t('config.tab_audio'),
            icon: <Volume2 size={18} />,
            content: <AudioSettings settings={settings} updateSetting={updateSetting} t={t} />
          },
          {
            id: 'display',
            label: t('config.tab_display'),
            icon: <Eye size={18} />,
            content: <DisplaySettings settings={settings} updateSetting={updateSetting} t={t} />
          },
          {
            id: 'gameplay',
            label: t('config.tab_gameplay'),
            icon: <Gamepad2 size={18} />,
            content: <GameplaySettings settings={settings} updateSetting={updateSetting} t={t} />
          },
          {
            id: 'theme',
            label: t('config.tab_theme'),
            icon: <Palette size={18} />,
            content: <ThemeSettings settings={settings} updateSetting={updateSetting} t={t} />
          },
        ]}
      />

      {/* Footer */}
      <div className="config-footer">
        <button
          className="button button-secondary button-small"
          onClick={resetToDefaults}
          data-sfx="button"
        >
          {t('config.reset_defaults')}
        </button>
        {isSaving && (
          <span className="config-saving">
            <Loader2 size={14} className="config-saving-icon" />
            {t('config.saving')}
          </span>
        )}
      </div>
    </Modal>
  );
}

// Audio Settings Tab
function AudioSettings({ settings, updateSetting, t }) {
  return (
    <div className="config-section">
      <div className="config-row">
        <label className="config-label">{t('config.sound_enabled')}</label>
        <Toggle
          checked={settings.sound_enabled}
          onChange={(checked) => updateSetting('sound_enabled', checked)}
        />
      </div>

      <div className="config-row">
        <label className="config-label">{t('config.music_volume')}</label>
        <div className="config-slider-group">
          <Slider
            variant="compact"
            value={settings.music_volume}
            onChange={(value) => updateSetting('music_volume', value)}
            min={0}
            max={100}
            disabled={!settings.sound_enabled}
          />
          <span className="config-value">{settings.music_volume}%</span>
        </div>
      </div>

      <div className="config-row">
        <label className="config-label">{t('config.sfx_volume')}</label>
        <div className="config-slider-group">
          <Slider
            variant="compact"
            value={settings.sfx_volume}
            onChange={(value) => updateSetting('sfx_volume', value)}
            min={0}
            max={100}
            disabled={!settings.sound_enabled}
          />
          <span className="config-value">{settings.sfx_volume}%</span>
        </div>
      </div>

    </div>
  );
}

// Display Settings Tab
function DisplaySettings({ settings, updateSetting, t }) {
  const locale = useTranslationStore((state) => state.locale);
  const setLocale = useTranslationStore((state) => state.setLocale);
  const isLoadingLocale = useTranslationStore((state) => state.isLoading);

  return (
    <div className="config-section">
      {/* Language selector */}
      <div className="config-row">
        <label className="config-label">{t('config.language')}</label>
        <select
          className="config-select"
          value={locale}
          onChange={(e) => setLocale(e.target.value)}
          disabled={isLoadingLocale}
        >
          {LANGUAGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.flag} {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="config-row">
        <label className="config-label">{t('config.animation_speed')}</label>
        <select
          className="config-select"
          value={settings.animation_speed}
          onChange={(e) => updateSetting('animation_speed', e.target.value)}
        >
          <option value="slow">{t('config.speed_slow')}</option>
          <option value="normal">{t('config.speed_normal')}</option>
          <option value="fast">{t('config.speed_fast')}</option>
          <option value="skip">{t('config.speed_skip')}</option>
        </select>
      </div>

      <div className="config-row">
        <label className="config-label">{t('config.show_damage_numbers')}</label>
        <Toggle
          checked={settings.show_damage_numbers}
          onChange={(checked) => updateSetting('show_damage_numbers', checked)}
        />
      </div>

      <div className="config-row">
        <label className="config-label">{t('config.show_tooltips')}</label>
        <Toggle
          checked={settings.show_tooltips}
          onChange={(checked) => updateSetting('show_tooltips', checked)}
        />
      </div>
    </div>
  );
}

// Gameplay Settings Tab
function GameplaySettings({ settings, updateSetting, t }) {
  return (
    <div className="config-section">
      <div className="config-row">
        <label className="config-label">
          {t('config.auto_end_turn')}
          <span className="config-sublabel">{t('config.auto_end_turn_desc')}</span>
        </label>
        <Toggle
          checked={settings.auto_end_turn}
          onChange={(checked) => updateSetting('auto_end_turn', checked)}
        />
      </div>

      <div className="config-row">
        <label className="config-label">
          {t('config.confirm_end_turn')}
          <span className="config-sublabel">{t('config.confirm_end_turn_desc')}</span>
        </label>
        <Toggle
          checked={settings.confirm_end_turn}
          onChange={(checked) => updateSetting('confirm_end_turn', checked)}
        />
      </div>
    </div>
  );
}

// Theme Settings Tab
function ThemeSettings({ settings, updateSetting, t }) {
  const [isLoading, setIsLoading] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);
  const currentTheme = settings.theme || 'base';
  const baseTheme = settings.baseTheme;

  // Get themes from metadata (backend), fallback to hardcoded if not available
  const metadataThemes = useMetadataStore((state) => state.metadata?.themes);
  const themes = metadataThemes || FALLBACK_THEMES;

  const handleThemeChange = async (themeSlug) => {
    if (themeSlug === currentTheme || isLoading) return;

    setIsLoading(true);
    try {
      const brand = import.meta.env.VITE_GAME_BRAND;
      await Asset.loadTheme(`/${brand}/theme/${themeSlug}/theme.json`, true);
      updateSetting('theme', themeSlug);
    } catch (error) {
      console.error('[ConfigModal] Failed to load theme:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStackChange = (newStack) => {
    // Save primary theme to settings
    if (newStack.length > 0) {
      updateSetting('theme', newStack[0]);
      updateSetting('themeStack', newStack);
    }
  };

  return (
    <div className="config-section">
      {/* Base theme indicator */}
      {baseTheme && (
        <p className="beta-warning" style={{ marginBottom: 'var(--space-md)' }}>
          <strong>{t('config.base_theme')}:</strong> {t(`config.theme_${baseTheme}`)}
        </p>
      )}

      {/* Mode toggle */}
      <div className="config-row config-theme-mode-toggle">
        <Tabs
          variant="segmented"
          activeTab={advancedMode ? 'stack' : 'simple'}
          onTabChange={(val) => setAdvancedMode(val === 'stack')}
          tabs={[
            { id: 'simple', label: t('config.theme_simple'), content: null },
            { id: 'stack', label: t('config.theme_stack'), icon: <Layers size={14} />, content: null },
          ]}
        />
      </div>

      {/* Simple mode - Theme grid */}
      {!advancedMode && (
        <div className="config-theme-grid">
          {themes.map((theme) => (
            <button
              key={theme.slug}
              className={`config-theme-card ${currentTheme === theme.slug ? 'active' : ''}`}
              onClick={() => handleThemeChange(theme.slug)}
              disabled={isLoading}
              data-sfx="button"
            >
              {/* Color Preview */}
              <div className="config-theme-preview">
                <div
                  className="config-theme-color"
                  style={{ backgroundColor: theme.preview.primary }}
                  title={t('config.color_primary')}
                />
                <div
                  className="config-theme-color"
                  style={{ backgroundColor: theme.preview.secondary }}
                  title={t('config.color_secondary')}
                />
                <div
                  className="config-theme-color"
                  style={{ backgroundColor: theme.preview.bg }}
                  title={t('config.color_background')}
                />
              </div>
              <div className="config-theme-header">
                <span className="config-theme-name">{t(theme.nameKey)}</span>
                {currentTheme === theme.slug && <Check size={16} className="config-theme-check" />}
              </div>
              <span className="config-theme-description">{t(theme.descKey)}</span>
              {isLoading && currentTheme !== theme.slug && (
                <Loader2 size={14} className="config-theme-loading" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Advanced mode - Theme Stack */}
      {advancedMode && (
        <ThemeStackEditor onStackChange={handleStackChange} />
      )}
    </div>
  );
}

